import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Label, hint and error around a control — the last unfinished Tier 1 item,
 * and the largest single gap in the library after `bz-icon`.
 *
 * The console wraps its controls in `md-input-container` **1881 times across
 * 534 files**, and **1483** of those carry a `<label>`. Every one of them
 * re-implements the same three things, which is why `bz-input` and
 * `bz-textarea` both shipped with no visible label and a note pointing here.
 *
 * ```html
 * <bz-form-field label="Description" hint="Shown on the service area card">
 *   <bz-textarea [(value)]="description" />
 * </bz-form-field>
 * ```
 *
 * ## The label is associated implicitly, by wrapping
 *
 * The rendered `<label>` wraps the projected control, so the browser
 * associates them with no `id` plumbing and no cooperation from the control —
 * verified against `bz-input`'s own DOM, where the `<input>` sits three
 * levels down and still reports `labels.length === 1`. Clicking the label
 * focuses the control for free, as it does for any native label.
 *
 * That is also why the hint and the error are rendered *outside* the
 * `<label>`: a label's accessible name is its entire text content, so a hint
 * inside it would be read as part of the field's name
 * ("Description Shown on the service area card").
 *
 * **Drop `ariaLabel` from a control you put in here** — with one exception
 * below. `aria-label` overrides the wrapping label, so leaving both sets the
 * name twice and the visible one loses.
 *
 * ## `bz-select` is the exception: keep its `ariaLabel`
 *
 * Implicit association only works for a *labelable* element — `input`,
 * `select`, `textarea`, `button`, `meter`, `output`, `progress`. Every
 * `bz-*` control renders one of those except `bz-select`, which is built on
 * `mat-select` and renders a `div[role="combobox"]`. A `div` is not
 * labelable, so the wrapping label does not reach it: measured in the
 * browser, such a select reports no associated label and takes its name from
 * `mat-select`'s own `aria-labelledby`, which points at the *selected value*.
 * The field then announces as "Delivery" rather than "Stop type".
 *
 * So a `bz-select` inside a `bz-form-field` still needs its own `ariaLabel`,
 * duplicating the visible caption. Removing that duplication means giving
 * `bz-select` an `ariaLabelledby` input and a generated id here — the same
 * `BzFormFieldControl` plumbing the hint needs, and worth doing once rather
 * than twice.
 *
 * ## Two stated limitations, not oversights
 *
 * - **The hint is not announced.** Wiring it as `aria-describedby` needs an
 *   id on the control, and neither `bz-input` nor `bz-textarea` exposes one.
 *   Doing it properly means a `BzFormFieldControl` token for the field to
 *   inject and drive — the machinery Material has — which is its own piece of
 *   work. The error *is* announced, through `role="alert"`, because that
 *   needs nothing from the control.
 * - **`required` is presentational.** It draws the asterisk the console draws;
 *   it cannot mark the control required, because that has to be on the
 *   control itself. Add `Validators.required` or the native attribute as
 *   well, or assistive technology will not know.
 */
@Component({
  selector: 'bz-form-field',
  templateUrl: './bz-form-field.html',
  styleUrl: './bz-form-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzFormField {
  readonly label = input('');

  /** Helper text under the control. Replaced by `error` while one is set. */
  readonly hint = input('');

  /** Shown in place of the hint, in the danger tone, and announced. */
  readonly error = input('');

  /**
   * Draws the asterisk. Presentational only — see the note on the class: the
   * control still has to be marked required for assistive technology.
   */
  readonly required = input(false);

  /** The error wins the one message slot, because it is the actionable one. */
  protected readonly message = computed(() => this.error() || this.hint());
}
